import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('adminToken');
    const storedUser = localStorage.getItem('adminUser');
    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
      }
    }
    setLoading(false);
  }, []);

  const loginStep1 = async (email, password) => {
    const response = await axios.post(`${API}/admin/login`, { email, password });
    const data = response.data;
    // If trusted device — login completes immediately
    if (!data.requires_2fa && data.access_token) {
      setToken(data.access_token);
      setUser(data.user);
      localStorage.setItem('adminToken', data.access_token);
      localStorage.setItem('adminUser', JSON.stringify(data.user));
    }
    return data;
  };

  const loginStep2 = async (tempToken, code, trustDevice = false) => {
    const response = await axios.post(`${API}/admin/verify-2fa`, {
      temp_token: tempToken,
      code,
      trust_device: trustDevice,
    });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    localStorage.setItem('adminToken', access_token);
    localStorage.setItem('adminUser', JSON.stringify(userData));
    return userData;
  };

  const resend2fa = async (tempToken) => {
    const response = await axios.post(`${API}/admin/resend-2fa`, {
      temp_token: tempToken,
    });
    return response.data;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
  };

  const getAuthHeaders = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const isAuthenticated = !!token && !!user;

  return (
    <AdminAuthContext.Provider value={{
      user,
      token,
      loading,
      isAuthenticated,
      loginStep1,
      loginStep2,
      resend2fa,
      logout,
      getAuthHeaders
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthContext;
